import { z } from 'zod';
import { idSchema, isoDateTimeSchema } from '../../common.js';
import { syncDocNameSchema } from '../../sync.js';
/**
 * Sharing circles (WS-15) — the permission layer at the doc-name/handshake boundary that
 * ROADMAP §2 Phase 4 reserved ("evolving from 'one household sees all docs' to 'each doc
 * has a circle of members' is a permission-layer change … not a rearchitecture").
 *
 * Two mechanisms, deliberately different in strength — see docs/DECISIONS.md 2026-08-01:
 *
 *  1. **Whole-doc audience (HARD, server-enforced).** Every doc may carry a `doc_access`
 *     row: an owner, an audience, and for `members` an explicit id list. The sync hub
 *     consults it on upgrade and refuses the socket (403) for anyone outside the circle,
 *     so a private list is private even against a hand-rolled WebSocket. A doc with **no**
 *     row is `family` — that is the back-compat default every doc written before WS-15 has.
 *
 *  2. **Per-user personal docs (HARD, by naming convention).** `calendar:user:<userId>`,
 *     `lists:user:<userId>`, `apptools:user:<userId>` — and, since WS-17c, the per-match
 *     shape `game:<id>:user:<userId>` — are readable only by `<userId>`. The rule is the
 *     *name*, so it needs no row and cannot be un-set by a bad write: it is what "Just me"
 *     writes into, what keeps private titles out of the shared registry, and what makes a
 *     hidden-information game (Battleship's ship placement) possible at all.
 *
 * Anything finer-grained than a doc (a single event inside the shared `calendar` doc) is
 * **soft**: UI-filtered only. See `calendarEventSchema.audience`.
 */
export const DOC_AUDIENCES = ['family', 'private', 'members'];
export const docAudienceSchema = z.enum(DOC_AUDIENCES);
/** Absent row = `family`. Every doc written before WS-15 therefore stays household-wide. */
export const DEFAULT_DOC_AUDIENCE = 'family';
/** Upper bound on an explicit circle — a household, not a mailing list. */
export const MAX_AUDIENCE_MEMBERS = 64;
/** One `doc_access` row. */
export const docAccessSchema = z.object({
    docName: syncDocNameSchema,
    /** The member who created the doc; the only one who may change its audience. */
    ownerId: idSchema,
    audience: docAudienceSchema,
    /** Only meaningful for `members`; always present (possibly empty) so reads are total. */
    memberIds: z.array(idSchema).max(MAX_AUDIENCE_MEMBERS).default([]),
    updatedAt: isoDateTimeSchema,
});
/** `PUT /api/sharing/:docName` body. */
export const docAccessUpdateSchema = z.object({
    audience: docAudienceSchema,
    memberIds: z.array(idSchema).max(MAX_AUDIENCE_MEMBERS).default([]),
});
/** `GET /api/sharing/:docName`. `access` is `null` when the doc has no row (= family). */
export const docAccessResponseSchema = z.object({
    docName: syncDocNameSchema,
    audience: docAudienceSchema,
    memberIds: z.array(idSchema),
    /** `null` for an unowned (never-shared) doc — the caller may claim it by writing. */
    ownerId: idSchema.nullable(),
    /** Whether the requesting member may change this doc's audience. */
    canManage: z.boolean(),
});
/** `GET /api/sharing` — every row that concerns me (mine, or one I'm a member of). */
export const docAccessListResponseSchema = z.object({
    entries: z.array(docAccessSchema),
});
/* -------------------------------------------------------------------------- */
/* Personal doc names                                                          */
/* -------------------------------------------------------------------------- */
/**
 * "Just me" calendar events live here rather than in the shared `calendar` doc, because a
 * whole-doc rule is the only kind the sync hub can enforce. Views merge the two docs
 * client-side.
 */
export const PERSONAL_CALENDAR_DOC_PREFIX = 'calendar:user:';
/**
 * Per-user list *registry*. A private or member-shared list's `ListMeta` moves out of the
 * shared `lists` doc into the owner's personal registry, so its **title** does not leak to
 * the household even though the content doc (`list:<id>`) is separately ACL'd.
 */
export const PERSONAL_LISTS_DOC_PREFIX = 'lists:user:';
/**
 * Per-user Apps registry (WS-16), the same shape for the exact same reason: a private or
 * member-shared NotePad/SketchPad's `AppToolMeta` moves out of the shared `apptools`
 * registry into the owner's `apptools:user:<id>` one, so its title doesn't sit in a doc
 * everyone can open.
 */
export const PERSONAL_APPTOOLS_DOC_PREFIX = 'apptools:user:';
const PERSONAL_DOC_PREFIXES = [
    PERSONAL_CALENDAR_DOC_PREFIX,
    PERSONAL_LISTS_DOC_PREFIX,
    PERSONAL_APPTOOLS_DOC_PREFIX,
];
/**
 * Per-match hidden information (WS-17c): `game:<id>:user:<userId>` belongs to `<userId>`
 * alone. Battleship's ship placement and commit salt live there — the hidden-information
 * game this whole enforcement layer was built for.
 *
 * Unlike the three above this is not a fixed prefix (the game id sits in the middle), so it
 * is a matcher rather than a `startsWith`. Deliberately kept **general**: the pattern is
 * "`<collection>:<id>:user:<uid>`", so a future kind with hidden state — a card hand, a Word
 * Duel secret word — reuses it by writing to the same shape, with no further change here.
 * The capture group is `[A-Za-z0-9_-]+` to match `syncDocNameSchema`'s segment alphabet, so
 * a name this accepts is always a name the hub would accept.
 */
export const PER_MATCH_USER_DOC_PATTERN = /^[a-z][a-z0-9-]*:[A-Za-z0-9_-]+:user:([A-Za-z0-9_-]+)$/;
export function personalCalendarDocName(userId) {
    return `${PERSONAL_CALENDAR_DOC_PREFIX}${userId}`;
}
export function personalListsDocName(userId) {
    return `${PERSONAL_LISTS_DOC_PREFIX}${userId}`;
}
export function personalAppToolsDocName(userId) {
    return `${PERSONAL_APPTOOLS_DOC_PREFIX}${userId}`;
}
/**
 * The member a personal doc belongs to, or `null` when `docName` is not a personal doc.
 * This is the whole of the naming-convention rule — the authorizer needs no database row
 * to enforce it, which is exactly why "Just me" is written as a doc name rather than as a
 * flag inside a shared doc.
 *
 * Two shapes qualify: a fixed personal prefix (`calendar:user:<uid>`, `lists:user:<uid>`,
 * `apptools:user:<uid>`) and the per-match hidden-state shape (`game:<id>:user:<uid>`,
 * WS-17c). Both answer the same question the same way, so both flow through the one function
 * every caller already uses — no new seam in the authorizer, no second rule to keep in sync.
 */
export function personalDocOwnerId(docName) {
    for (const prefix of PERSONAL_DOC_PREFIXES) {
        if (docName.startsWith(prefix)) {
            const owner = docName.slice(prefix.length);
            return owner.length > 0 ? owner : null;
        }
    }
    const perMatch = PER_MATCH_USER_DOC_PATTERN.exec(docName);
    return perMatch?.[1] ?? null;
}
export function isPersonalDocName(docName) {
    return personalDocOwnerId(docName) !== null;
}
/**
 * May `userId` open `docName`? The single source of truth, shared by the sync authorizer
 * and the REST surface so the two can never drift.
 *
 * Order matters: the name convention wins over any row, so a `doc_access` row can never
 * widen access to somebody else's `calendar:user:` doc.
 */
export function canAccessDoc({ docName, userId, access }) {
    const personalOwner = personalDocOwnerId(docName);
    if (personalOwner !== null)
        return personalOwner === userId;
    if (!access)
        return true; // no row = family = every authenticated household member
    switch (access.audience) {
        case 'family':
            return true;
        case 'private':
            return access.ownerId === userId;
        case 'members':
            return access.ownerId === userId || access.memberIds.includes(userId);
    }
}
/**
 * May `userId` change this doc's audience? The owner, or anybody when the doc is not yet
 * owned — the first member to set an audience claims the doc (it is theirs: they made it).
 */
export function canManageDocAccess(access, userId) {
    if (!access)
        return true;
    return access.ownerId === userId;
}
/** The audience a doc effectively has, given its (possibly absent) row. */
export function effectiveAudience(access) {
    return access?.audience ?? DEFAULT_DOC_AUDIENCE;
}
/** Human copy for the picker and for "shared with…" notes. Kept next to the enum. */
export function describeAudience(audience, memberCount = 0) {
    switch (audience) {
        case 'private':
            return 'Just me';
        case 'members':
            return memberCount === 0
                ? 'Some people'
                : `${memberCount} ${memberCount === 1 ? 'person' : 'people'}`;
        case 'family':
            return 'Everyone';
    }
}
//# sourceMappingURL=index.js.map